import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { InputTags } from "@/components/form/input-tags";
import { PhoneInput } from "@/components/form/phone-input";
import { StateSelect } from "@/components/form/state-select";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { Fragment } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAuth } from "@/providers/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateContact } from "@/pages/contacts/actions";

const emailRules = {
  required: { value: false },
  pattern: {
    message: "Email format is invalid",
    value:
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  },
};

const firstNameRules = {
  required: { message: "First name is required", value: true },
};

const lastNameRules = {
  required: { message: "Last name is required", value: true },
};

const phoneRules = {
  required: { message: "Phone is required", value: true },
};

const contactIdRules = {
  maxLength: { value: 25, message: "Contact ID must be 25 characters or less" },
  pattern: { value: /^\S*$/, message: "Contact ID cannot contain spaces" },
  validate: (value) => {
    if (!value) return true;
    const sqlInjectionPattern =
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|SCRIPT|--|;|\/\*|\*\/)\b)|['"]/i;
    return !sqlInjectionPattern.test(value) || "Invalid characters in Contact ID";
  },
};

function noop() {}

export function EditContactDrawer({ open, handleClose, contact, onSuccess = noop }) {
  const { toast } = useToast();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const queryClient = useQueryClient();

  const { mutate, isLoading: updatingContact } = useMutation({
    mutationFn: (payload) =>
      updateContact({ userId, contactId: contact?.contact_id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries(["contacts"]);
    },
  });

  const info = contact?.contact_info || {};

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm({
    mode: "onChange",
    values: {
      firstName: info.first_name || "",
      lastName: info.last_name || "",
      email: info.email || "",
      phone: info.phone || "",
      tags: info.tags || [],
      contactId: info.contact_ref_id || "",
      businessName: info.business_name || "",
      businessAddress: info.business_address || "",
      businessCity: info.business_city || "",
      businessState: info.business_state || "",
      businessZip: info.business_zip || "",
      businessCountry: info.business_country || "",
    },
  });

  const onSubmit = handleSubmit(
    async ({
      firstName,
      lastName,
      email,
      businessName,
      phone,
      tags,
      contactId,
      businessAddress,
      businessCity,
      businessState,
      businessCountry,
      businessZip,
    }) => {
      let clientCognitoId;
      try {
        clientCognitoId = JSON.parse(localStorage.getItem("PLATFORM_ID"));
      } catch {
        // ignore parse error, handled below
      }
      if (!clientCognitoId) {
        toast({
          variant: "destructive",
          title: "Unable to identify the platform. Please refresh and try again.",
          duration: 4000,
        });
        return;
      }
      const payload = {
        cognito_id: clientCognitoId,
        first_name: firstName,
        last_name: lastName,
        email: email || null,
        phone,
        tags,
        contact_ref_id: contactId?.trim() || "",
        business_name: businessName,
        business_address: businessAddress,
        business_city: businessCity,
        business_state: businessState,
        business_zip: businessZip,
        business_country: businessCountry,
        notes: [],
        integrations: {},
      };

      mutate(payload, {
        onSuccess: (data) => {
          onSuccess(data);
          handleClose();
          toast({
            variant: "success",
            title: "Contact updated successfully!",
            duration: 4000,
          });
        },
        onError: (error) => {
          if (error?.response?.status === 422) {
            toast({
              variant: "destructive",
              title: "There was one error with your submission.",
              description: error?.response?.data?.detail,
              duration: 3000,
            });
          } else if (error?.response?.status === 409) {
            toast({
              variant: "destructive",
              title: "You already have a contact with this contact ID #.",
              duration: 5000,
            });
          } else {
            toast({
              variant: "destructive",
              title: "Uh oh! Something went wrong.",
              description: "Please try submitting again.",
              duration: 3000,
            });
          }
        },
      });
    }
  );

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            handleClose();
          }}
        >
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                    <div className="pb-[120px] flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Edit Contact
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                              onClick={() => {
                                handleClose();
                                reset();
                              }}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <form className="space-y-12 pl-6 pr-8 py-8" onSubmit={onSubmit}>
                        <div className="border-b border-gray-900/10 pb-12">
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            Personal Information
                          </h2>

                          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                              <Label htmlFor="first-name">
                                First Name <span className="text-red-700">*</span>
                              </Label>
                              <div className="mt-2">
                                <Input
                                  id="first-name"
                                  type="text"
                                  autoComplete="given-name"
                                  {...register("firstName", firstNameRules)}
                                />
                              </div>
                              {errors.firstName && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.firstName.message}
                                </p>
                              )}
                            </div>

                            <div className="sm:col-span-3">
                              <Label htmlFor="last-name">
                                Last Name <span className="text-red-700">*</span>
                              </Label>
                              <div className="mt-2">
                                <Input
                                  id="last-name"
                                  type="text"
                                  autoComplete="family-name"
                                  {...register("lastName", lastNameRules)}
                                />
                              </div>
                              {errors.lastName && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.lastName.message}
                                </p>
                              )}
                            </div>

                            <div className="sm:col-span-4">
                              <Label htmlFor="email">Email Address</Label>
                              <div className="mt-2">
                                <Input
                                  id="email"
                                  autoComplete="email"
                                  {...register("email", emailRules)}
                                />
                              </div>
                              <p className="text-sm text-slate-500 mt-1">
                                Invoices and quotes will be sent to this email address
                              </p>
                              {errors.email && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.email.message}
                                </p>
                              )}
                            </div>

                            <div className="sm:col-span-3">
                              <Label htmlFor="phone">
                                Phone <span className="text-red-700">*</span>
                              </Label>
                              <div className="mt-2">
                                <Controller
                                  control={control}
                                  name="phone"
                                  rules={phoneRules}
                                  render={({ field }) => (
                                    <PhoneInput
                                      id="phone"
                                      autoComplete="phone"
                                      value={field.value}
                                      onChange={field.onChange}
                                    />
                                  )}
                                />
                              </div>
                              {errors.phone && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.phone.message}
                                </p>
                              )}
                            </div>

                            <div className="sm:col-span-3">
                              <Label htmlFor="contact-id">Contact ID #</Label>
                              <div className="mt-2">
                                <Input
                                  id="contact-id"
                                  type="text"
                                  {...register("contactId", contactIdRules)}
                                />
                              </div>
                              {errors.contactId && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.contactId.message}
                                </p>
                              )}
                            </div>

                            <div className="col-span-full">
                              <label
                                htmlFor="tags"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Tags
                              </label>
                              <div className="mt-2">
                                <Controller
                                  control={control}
                                  name="tags"
                                  render={({ field }) => (
                                    <InputTags
                                      id="tags"
                                      value={field.value}
                                      onChange={field.onChange}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="border-b border-gray-900/10 pb-12">
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            Business Information
                          </h2>

                          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                              <Label htmlFor="businessName">Business Name</Label>
                              <div className="mt-2">
                                <Input
                                  id="business-name"
                                  type="text"
                                  disabled={!!info.business_name}
                                  {...register("businessName")}
                                />
                              </div>
                            </div>

                            <div className="col-span-full">
                              <Label htmlFor="business-address">Physical Address</Label>
                              <div className="mt-2">
                                <Input
                                  id="business-address"
                                  type="text"
                                  {...register("businessAddress")}
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2 sm:col-start-1">
                              <Label htmlFor="city">City</Label>
                              <div className="mt-2">
                                <Input
                                  id="city"
                                  type="text"
                                  {...register("businessCity")}
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2">
                              <Label htmlFor="state">State</Label>
                              <div className="mt-2">
                                <Controller
                                  name="businessState"
                                  control={control}
                                  render={({ field }) => <StateSelect {...field} />}
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2">
                              <Label htmlFor="postal-code">ZIP / Postal code</Label>
                              <div className="mt-2">
                                <Input
                                  id="postal-code"
                                  type="text"
                                  {...register("businessZip", {
                                    maxLength: {
                                      value: 10,
                                      message: "ZIP code must be 10 characters or less (e.g. 12345-6789)",
                                    },
                                  })}
                                />
                              </div>
                              {errors.businessZip && (
                                <p role="alert" className="text-sm text-red-600 mt-1">
                                  {errors.businessZip.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            className="mr-4"
                            onClick={() => {
                              handleClose();
                              reset();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button disabled={isSubmitting || updatingContact} type="submit">
                            {(isSubmitting || updatingContact) && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Update Contact
                          </Button>
                        </div>
                      </form>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
